import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
	FaPlus,
	FaTrash,
	FaEdit,
	FaCheckCircle,
	FaRegCircle,
	FaClock,
	FaSort,
	FaCalendarAlt,
} from 'react-icons/fa';

import { useAuth } from '../contexts/auth_context';
import TodoService from '../services/todo_service';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const isOverdueTask = todo =>
	!todo.completed && todo.dueDate && new Date(todo.dueDate) < new Date();

const compareByPriority = (a, b) => {
	const order = { high: 0, medium: 1, low: 2 };
	return (order[a.priority] ?? 1) - (order[b.priority] ?? 1);
};

const compareByDueDate = (a, b) => {
	if (a.dueDate && !b.dueDate) return -1;
	if (!a.dueDate && b.dueDate) return 1;
	if (a.dueDate && b.dueDate) return new Date(a.dueDate) - new Date(b.dueDate);
	return 0;
};

const SORT_OPTIONS = [
	{ value: 'default', label: 'Default' },
	{ value: 'dueDate', label: 'Due date' },
	{ value: 'priority', label: 'Priority' },
];

const formatDate = dateStr => {
	if (!dateStr) return null;
	return new Date(dateStr).toLocaleDateString('en-GB', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
	});
};

// ─── Priority badge ───────────────────────────────────────────────────────────

const PRIORITY_STYLES = {
	high: {
		bg: 'rgba(255,11,120,0.12)',
		color: '#FF0B78',
		border: 'rgba(255,11,120,0.3)',
		label: 'High',
	},
	medium: {
		bg: 'rgba(245,158,11,0.12)',
		color: '#F59E0B',
		border: 'rgba(245,158,11,0.3)',
		label: 'Medium',
	},
	low: {
		bg: 'rgba(34,197,94,0.12)',
		color: '#22C55E',
		border: 'rgba(34,197,94,0.3)',
		label: 'Low',
	},
};

const PriorityBadge = ({ priority }) => {
	const s = PRIORITY_STYLES[priority] ?? PRIORITY_STYLES.medium;
	return (
		<span
			className='flex-shrink-0 text-[11px] font-semibold px-2.5 py-0.5 rounded-full border'
			style={{ background: s.bg, color: s.color, borderColor: s.border }}
		>
			{s.label}
		</span>
	);
};

PriorityBadge.propTypes = { priority: PropTypes.string.isRequired };

// ─── TodoRow ──────────────────────────────────────────────────────────────────

const TodoRow = ({ todo, onToggle, onEdit, onDelete }) => {
	const overdue = isOverdueTask(todo);

	return (
		<div
			className='flex items-center gap-3 px-4 py-3 rounded-xl border relative overflow-hidden transition-all group'
			style={
				overdue
					? {
							background: 'rgba(255,11,120,0.05)',
							borderColor: 'rgba(255,11,120,0.2)',
							opacity: todo.completed ? 0.5 : 1,
						}
					: {
							background: 'var(--app-surface-muted)',
							borderColor: 'var(--app-border)',
							opacity: todo.completed ? 0.5 : 1,
						}
			}
		>
			{/* Overdue left accent */}
			{overdue && (
				<span
					className='absolute left-0 top-0 bottom-0 w-[3px] rounded-l-xl'
					style={{ background: 'linear-gradient(180deg, #FF0B78, #E300C9)' }}
				/>
			)}

			{/* Checkbox */}
			<button
				type='button'
				onClick={() => onToggle(todo.id)}
				title={todo.completed ? 'Mark incomplete' : 'Mark complete'}
				className='flex-shrink-0 flex items-center justify-center transition-opacity hover:opacity-70'
			>
				{todo.completed ? (
					<FaCheckCircle size={18} style={{ color: '#22C55E' }} />
				) : (
					<FaRegCircle size={18} style={{ color: 'var(--app-text-muted)' }} />
				)}
			</button>

			{/* Body */}
			<div className='flex flex-col gap-0.5 flex-1 min-w-0'>
				<span
					className='text-[13px] font-medium truncate'
					style={{
						color: todo.completed ? 'var(--app-text-muted)' : 'var(--app-text)',
						textDecoration: todo.completed ? 'line-through' : 'none',
					}}
				>
					{todo.title}
				</span>
				{todo.description && (
					<span
						className='text-[11px] truncate'
						style={{ color: 'var(--app-text-muted)' }}
					>
						{todo.description}
					</span>
				)}
			</div>

			{/* Priority */}
			<PriorityBadge priority={todo.priority} />

			{/* Due date */}
			{todo.dueDate ? (
				<div
					className='flex-shrink-0 flex items-center gap-1.5 text-[12px] min-w-[100px]'
					style={{ color: overdue ? '#FF0B78' : 'var(--app-text-muted)' }}
				>
					{overdue ? <FaClock size={11} /> : <FaCalendarAlt size={11} />}
					<span>{formatDate(todo.dueDate)}</span>
				</div>
			) : (
				<div className='flex-shrink-0 min-w-[100px]' />
			)}

			{/* Actions — revealed on row hover */}
			<div className='flex gap-1.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity'>
				{!todo.completed && (
					<button
						type='button'
						onClick={() => onEdit(todo)}
						title='Edit'
						className='flex items-center justify-center w-7 h-7 rounded-lg border transition-all'
						style={{
							background: 'var(--app-surface)',
							borderColor: 'var(--app-border)',
							color: 'var(--app-text-muted)',
						}}
						onMouseEnter={e => {
							e.currentTarget.style.color = '#018DBB';
							e.currentTarget.style.borderColor = '#018DBB';
						}}
						onMouseLeave={e => {
							e.currentTarget.style.color = 'var(--app-text-muted)';
							e.currentTarget.style.borderColor = 'var(--app-border)';
						}}
					>
						<FaEdit size={12} />
					</button>
				)}
				<button
					type='button'
					onClick={() => onDelete(todo.id)}
					title='Delete'
					className='flex items-center justify-center w-7 h-7 rounded-lg border transition-all'
					style={{
						background: 'var(--app-surface)',
						borderColor: 'var(--app-border)',
						color: 'var(--app-text-muted)',
					}}
					onMouseEnter={e => {
						e.currentTarget.style.color = '#FF0B78';
						e.currentTarget.style.borderColor = 'rgba(255,11,120,0.4)';
					}}
					onMouseLeave={e => {
						e.currentTarget.style.color = 'var(--app-text-muted)';
						e.currentTarget.style.borderColor = 'var(--app-border)';
					}}
				>
					<FaTrash size={12} />
				</button>
			</div>
		</div>
	);
};

TodoRow.propTypes = {
	todo: PropTypes.shape({
		id: PropTypes.string.isRequired,
		title: PropTypes.string.isRequired,
		description: PropTypes.string,
		priority: PropTypes.string.isRequired,
		dueDate: PropTypes.string,
		completed: PropTypes.bool.isRequired,
	}).isRequired,
	onToggle: PropTypes.func.isRequired,
	onEdit: PropTypes.func.isRequired,
	onDelete: PropTypes.func.isRequired,
};

// ─── Section ──────────────────────────────────────────────────────────────────

const Section = ({ label, todos, onToggle, onEdit, onDelete }) => {
	if (todos.length === 0) return null;
	return (
		<div className='mb-5'>
			<p
				className='text-[10px] font-bold tracking-[0.1em] mb-2 px-1'
				style={{ color: 'var(--app-text-muted)' }}
			>
				{label}
			</p>
			<div className='flex flex-col gap-2'>
				{todos.map(todo => (
					<TodoRow
						key={todo.id}
						todo={todo}
						onToggle={onToggle}
						onEdit={onEdit}
						onDelete={onDelete}
					/>
				))}
			</div>
		</div>
	);
};

Section.propTypes = {
	label: PropTypes.string.isRequired,
	todos: PropTypes.array.isRequired,
	onToggle: PropTypes.func.isRequired,
	onEdit: PropTypes.func.isRequired,
	onDelete: PropTypes.func.isRequired,
};

// ─── TodoView ─────────────────────────────────────────────────────────────────

const EMPTY_FORM = { title: '', priority: 'medium', dueDate: '', description: '' };

const TodoView = () => {
	const { user } = useAuth();
	const [todos, setTodos] = useState([]);
	const [showForm, setShowForm] = useState(false);
	const [editingId, setEditingId] = useState(null);
	const [filterStatus, setFilterStatus] = useState('all');
	const [sortBy, setSortBy] = useState('default');
	const [showSortMenu, setShowSortMenu] = useState(false);
	const [inlineTitle, setInlineTitle] = useState('');
	const [formData, setFormData] = useState(EMPTY_FORM);
	const dueDateInputRef = useRef(null);

	const openDueDatePicker = useCallback(() => {
		if (!dueDateInputRef.current) return;
		try {
			if (typeof dueDateInputRef.current.showPicker === 'function') {
				dueDateInputRef.current.showPicker();
			} else {
				dueDateInputRef.current.focus();
			}
		} catch {
			dueDateInputRef.current.focus();
		}
	}, []);

	const todoService = useMemo(() => (user ? new TodoService(user.uid) : null), [user]);

	useEffect(() => {
		if (user && todoService) {
			todoService
				.getTodos()
				.then(setTodos)
				.catch(() => setTodos([]));
		}
	}, [user, todoService]);

	const handleAddTodo = async e => {
		e.preventDefault();
		if (!formData.title.trim() || !todoService) return;
		if (editingId) {
			const updated = await todoService.updateTodo(editingId, formData);
			if (updated) setTodos(prev => prev.map(t => (t.id === editingId ? updated : t)));
			setEditingId(null);
		} else {
			const newTodo = await todoService.createTodo(formData);
			if (newTodo) setTodos(prev => [...prev, newTodo]);
		}
		setFormData(EMPTY_FORM);
		setShowForm(false);
	};

	const handleInlineAdd = async e => {
		e.preventDefault();
		if (!inlineTitle.trim() || !todoService) return;
		const newTodo = await todoService.createTodo({ ...EMPTY_FORM, title: inlineTitle });
		if (newTodo) setTodos(prev => [...prev, newTodo]);
		setInlineTitle('');
	};

	const handleDeleteTodo = async id => {
		if (!todoService) return;
		const ok = await todoService.deleteTodo(id);
		if (ok) setTodos(prev => prev.filter(t => t.id !== id));
	};

	const handleToggleTodo = async id => {
		if (!todoService) return;
		const updated = await todoService.toggleTodo(id);
		if (updated) setTodos(prev => prev.map(t => (t.id === id ? updated : t)));
	};

	const handleEditTodo = todo => {
		setEditingId(todo.id);
		setFormData({
			title: todo.title,
			priority: todo.priority,
			dueDate: todo.dueDate ? todo.dueDate.split('T')[0] : '',
			description: todo.description,
		});
		setShowForm(true);
	};

	const handleCancelEdit = () => {
		setEditingId(null);
		setFormData(EMPTY_FORM);
		setShowForm(false);
	};

	const sortedFiltered = useMemo(() => {
		let list = [...todos];
		if (filterStatus === 'active') list = list.filter(t => !t.completed);
		else if (filterStatus === 'completed') list = list.filter(t => t.completed);
		if (sortBy === 'dueDate') list.sort(compareByDueDate);
		else if (sortBy === 'priority') list.sort(compareByPriority);
		return list;
	}, [todos, filterStatus, sortBy]);

	const overdueTodos = sortedFiltered.filter(t => isOverdueTask(t));
	const upcomingTodos = sortedFiltered.filter(t => !t.completed && !isOverdueTask(t));
	const doneTodos = sortedFiltered.filter(t => t.completed);
	const overdueCount = todos.filter(t => isOverdueTask(t)).length;
	const activeSortLabel = SORT_OPTIONS.find(o => o.value === sortBy)?.label;

	let emptyMessage = 'No todos yet. Create one to get started!';
	if (filterStatus === 'active') emptyMessage = 'No active todos. Great job! 🎉';
	else if (filterStatus === 'completed') emptyMessage = 'No completed todos yet.';

	// ── Shared input style (uses CSS vars → works in both themes) ──────────────
	const inputStyle = {
		background: 'var(--app-input-bg)',
		border: '1px solid var(--app-border)',
		borderRadius: 8,
		padding: '9px 12px',
		fontSize: 13,
		color: 'var(--app-text)',
		fontFamily: 'inherit',
		width: '100%',
		boxSizing: 'border-box',
		outline: 'none',
		transition: 'border-color 0.15s',
	};

	const focusBorder = e => (e.target.style.borderColor = '#FF0B78');
	const blurBorder = e => (e.target.style.borderColor = 'var(--app-border)');

	return (
		<div
			className='min-h-[calc(100vh-96px)] p-6 md:p-8 font-inter'
			style={{ color: 'var(--app-text)' }}
		>
			{/* ── Form ───────────────────────────────────────────────────────── */}
			{showForm && (
				<div
					className='rounded-xl border p-5 mb-6'
					style={{ background: 'var(--app-surface)', borderColor: 'var(--app-border)' }}
				>
					<form onSubmit={handleAddTodo}>
						<div
							className='grid gap-3 mb-4'
							style={{ gridTemplateColumns: '2fr 1fr 1fr' }}
						>
							<input
								style={inputStyle}
								type='text'
								placeholder='Todo title *'
								value={formData.title}
								onChange={e => setFormData({ ...formData, title: e.target.value })}
								onFocus={focusBorder}
								onBlur={blurBorder}
								required
							/>

							<select
								style={{ ...inputStyle, cursor: 'pointer' }}
								value={formData.priority}
								onChange={e =>
									setFormData({ ...formData, priority: e.target.value })
								}
								onFocus={focusBorder}
								onBlur={blurBorder}
							>
								<option value='low'>Low Priority</option>
								<option value='medium'>Medium Priority</option>
								<option value='high'>High Priority</option>
							</select>

							{/* Date picker */}
							<div style={{ position: 'relative' }}>
								<input
									ref={dueDateInputRef}
									type='date'
									value={formData.dueDate}
									onChange={e =>
										setFormData({ ...formData, dueDate: e.target.value })
									}
									tabIndex={-1}
									aria-hidden='true'
									style={{
										position: 'absolute',
										right: 10,
										top: '50%',
										transform: 'translateY(-50%)',
										width: 18,
										height: 18,
										opacity: 0,
										pointerEvents: 'none',
										margin: 0,
										padding: 0,
										border: 0,
									}}
								/>
								<button
									type='button'
									onClick={openDueDatePicker}
									style={{
										...inputStyle,
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'space-between',
										gap: 8,
										cursor: 'pointer',
									}}
									onFocus={focusBorder}
									onBlur={blurBorder}
								>
									<span
										style={{
											color: formData.dueDate
												? 'var(--app-text)'
												: 'var(--app-text-muted)',
										}}
									>
										{formData.dueDate
											? formatDate(formData.dueDate)
											: 'Due date'}
									</span>
									<FaCalendarAlt
										size={13}
										style={{ color: 'var(--app-text-muted)', flexShrink: 0 }}
									/>
								</button>
							</div>

							<textarea
								style={{
									...inputStyle,
									gridColumn: '1 / -1',
									resize: 'vertical',
									minHeight: 56,
								}}
								placeholder='Description (optional)'
								value={formData.description}
								onChange={e =>
									setFormData({ ...formData, description: e.target.value })
								}
								onFocus={focusBorder}
								onBlur={blurBorder}
								rows={2}
							/>
						</div>

						<div className='flex justify-end gap-2'>
							<button
								type='submit'
								className='px-4 py-2 rounded-lg text-[13px] font-semibold text-white transition-opacity hover:opacity-80'
								style={{ background: 'linear-gradient(90deg, #FF0B78, #E300C9)' }}
							>
								{editingId ? 'Update Todo' : 'Create Todo'}
							</button>
							<button
								type='button'
								onClick={handleCancelEdit}
								className='px-4 py-2 rounded-lg text-[13px] font-medium border transition-all'
								style={{
									background: 'transparent',
									borderColor: 'var(--app-border)',
									color: 'var(--app-text-muted)',
								}}
								onMouseEnter={e => {
									e.currentTarget.style.color = 'var(--app-text)';
									e.currentTarget.style.borderColor = 'var(--app-text-muted)';
								}}
								onMouseLeave={e => {
									e.currentTarget.style.color = 'var(--app-text-muted)';
									e.currentTarget.style.borderColor = 'var(--app-border)';
								}}
							>
								Cancel
							</button>
						</div>
					</form>
				</div>
			)}

			{/* ── Toolbar ─────────────────────────────────────────────────────── */}
			<div className='flex items-center justify-between gap-3 mb-6 flex-wrap'>
				{/* Filter chips + counts */}
				<div className='flex items-center gap-2 flex-wrap'>
					{overdueCount > 0 && (
						<span
							className='text-[11px] font-semibold px-2 py-0.5 rounded-full border'
							style={{
								background: 'rgba(255,11,120,0.08)',
								borderColor: 'rgba(255,11,120,0.25)',
								color: '#FF0B78',
							}}
						>
							{overdueCount} overdue
						</span>
					)}
				</div>
				{/* Filter chips */}
				<div className='flex gap-2 flex-wrap'>
					{[
						{ key: 'all', label: `All (${todos.length})` },
						{
							key: 'active',
							label: `Active (${todos.filter(t => !t.completed).length})`,
						},
						{
							key: 'completed',
							label: `Done (${todos.filter(t => t.completed).length})`,
						},
					].map(f => {
						const active = filterStatus === f.key;
						return (
							<button
								key={f.key}
								onClick={() => setFilterStatus(f.key)}
								className='px-3.5 py-1.5 rounded-lg text-[12px] font-medium border transition-all'
								style={{
									background: active
										? 'var(--app-surface)'
										: 'var(--app-surface-muted)',
									borderColor: active
										? 'var(--app-text-muted)'
										: 'var(--app-border)',
									color: active ? 'var(--app-text)' : 'var(--app-text-muted)',
								}}
							>
								{f.label}
							</button>
						);
					})}
				</div>

				{/* Sort + Add */}
				<div className='flex items-center gap-2'>
					<div className='relative'>
						<button
							onClick={() => setShowSortMenu(v => !v)}
							className='flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-[12px] font-medium border transition-all'
							style={{
								background: 'var(--app-surface-muted)',
								borderColor: 'var(--app-border)',
								color: 'var(--app-text-muted)',
							}}
						>
							<FaSort size={10} /> {activeSortLabel}
						</button>
						{showSortMenu && (
							<div
								className='absolute right-0 top-[calc(100%+6px)] rounded-xl border overflow-hidden z-50 min-w-[150px]'
								style={{
									background: 'var(--app-surface)',
									borderColor: 'var(--app-border)',
									boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
								}}
							>
								{SORT_OPTIONS.map(opt => (
									<button
										key={opt.value}
										className='block w-full text-left px-4 py-2.5 text-[12px] font-medium transition-colors'
										style={{
											color:
												sortBy === opt.value
													? '#FF0B78'
													: 'var(--app-text-muted)',
											background: 'transparent',
											border: 'none',
										}}
										onMouseEnter={e =>
											(e.currentTarget.style.background =
												'var(--app-surface-muted)')
										}
										onMouseLeave={e =>
											(e.currentTarget.style.background = 'transparent')
										}
										onClick={() => {
											setSortBy(opt.value);
											setShowSortMenu(false);
										}}
									>
										{opt.label}
									</button>
								))}
							</div>
						)}
					</div>
					<button
						onClick={() => {
							setEditingId(null);
							setFormData(EMPTY_FORM);
							setShowForm(v => !v);
						}}
						className='flex items-center gap-2 px-4 py-2 rounded-xl text-[12px] font-semibold text-white transition-opacity hover:opacity-80 flex-shrink-0'
						style={{ background: 'linear-gradient(90deg, #FF0B78, #E300C9)' }}
					>
						<FaPlus size={10} /> Add todo
					</button>
				</div>
			</div>

			{/* ── Todo list ───────────────────────────────────────────────────── */}
			<div className='flex flex-col'>
				{sortedFiltered.length === 0 ? (
					<div
						className='flex items-center justify-center py-16 rounded-xl border text-[14px]'
						style={{
							background: 'var(--app-surface-muted)',
							borderColor: 'var(--app-border)',
							color: 'var(--app-text-muted)',
						}}
					>
						{emptyMessage}
					</div>
				) : (
					<>
						<Section
							label='OVERDUE'
							todos={overdueTodos}
							onToggle={handleToggleTodo}
							onEdit={handleEditTodo}
							onDelete={handleDeleteTodo}
						/>
						<Section
							label='UPCOMING'
							todos={upcomingTodos}
							onToggle={handleToggleTodo}
							onEdit={handleEditTodo}
							onDelete={handleDeleteTodo}
						/>
						<Section
							label='DONE'
							todos={doneTodos}
							onToggle={handleToggleTodo}
							onEdit={handleEditTodo}
							onDelete={handleDeleteTodo}
						/>
					</>
				)}

				{/* Inline quick-add */}
				{filterStatus !== 'completed' && (
					<form
						onSubmit={handleInlineAdd}
						className='flex items-center gap-3 px-4 py-3 rounded-xl border mt-2 transition-all'
						style={{
							background: 'transparent',
							borderColor: 'var(--app-border)',
							borderStyle: 'dashed',
						}}
						onFocus={e => (e.currentTarget.style.borderColor = '#FF0B78')}
						onBlur={e => (e.currentTarget.style.borderColor = 'var(--app-border)')}
					>
						<FaPlus
							size={11}
							style={{ color: 'var(--app-text-muted)', flexShrink: 0 }}
						/>
						<input
							type='text'
							placeholder='Quick add a task…'
							value={inlineTitle}
							onChange={e => setInlineTitle(e.target.value)}
							style={{
								flex: 1,
								background: 'none',
								border: 'none',
								outline: 'none',
								fontSize: 13,
								color: 'var(--app-text)',
								fontFamily: 'inherit',
							}}
						/>
					</form>
				)}
			</div>
		</div>
	);
};

export default TodoView;
